import {useRef, useEffect, useState} from 'react'

/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {shuffle} from 'lodash'

import {connect, useDispatch} from 'react-redux'

import {show} from '../../apis'

import {
  enqueue,
  getAndQueueTracks, 
  setClickedMarkerData,
  addTracksToPerformance
} from '../../actions'


import {convertAppleMusicTracksToCkordFormat} from '../../helpers'

import {useMaps, useCreateMapMarkers} from '../../hooks'

import {useNavigate} from 'react-router-dom'

import CloseIcon from '@mui/icons-material/Close'

import '../../styles/google/mapInfoWindow.css'
import '../../styles/google/mapVenueInfoWindow.scss'
import '../../styles/google/mapPerformanceInfoWindow.scss'



const styles = {
  root: css`
    position: relative;
    height: 100%;
    width: 100%; 
  `,
  noticeContainer: css`
    position: absolute;
    z-index: 50;
    height: 40px;
    display: flex;
    align-items: center;
    top: 5px;
    left: 5px;
    background-color: #fff;
    padding: 10px 15px;
    border-radius: 5px;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;

    @media (max-width: 1200px) {
      top: 65px;
    }
  `,
  text: css`
    font-size: 0.83rem;
  `,
  iconContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 10px;
    height: 100%;
  `,
  map: css`
    width: 100%;
    height: 100%;
  `
}





const GoogleMap = props => {

  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'))

  const navigate = useNavigate()

  const dispatch = useDispatch()

  const mapOuterContainerRef = useRef()


  // This is used to find the Google logo and push it up
  const retryCountRef = useRef(0)
  const maxRetries = 50

  
  const [hasScrollNoticeBeenShown, setHasScrollNoticeBeenShown] = useState(
    window.localStorage.getItem('noticeShown') ||
    window.localStorage.getItem('hasScrollNoticeBeenShown')
  )


  const {
    enqueue, 
    mapCenter,
    // userLocation,
    getAndQueueTracks, 
    addTracksToPerformance
  } = props



  // Scroll notificaiton in top left hand corner of map
  useEffect(() => {
    if (!hasScrollNoticeBeenShown) {
      window.localStorage.setItem('hasScrollNoticeBeenShown', true)
    }
  }, [hasScrollNoticeBeenShown])



  // This loads the map and save the map object in the redux store.
  // On subseuquent renders, it loads the map from the store.
  const map = useMaps()

  // Create the map markers for venues and performances
  useCreateMapMarkers(map)



  // When a user navigates away from the home page, the map element gets
  // removed from the DOM. This effect recreates the map element using the 
  // map.getDiv() method from the map object that's stored in the redux state.
  // useEffect(() => {
  //   if (!map) return

  //   // Get the current map container. We need to use document.getElementById because
  //   // when a user navigates somewhere and comes back, we replace the current map container
  //   // with the one that the map has a reference to in container.replaceWith(map.getDiv()). If
  //   // we used a useRef hook then it would get lost in the replacement.
  //   // 
  //   // Replace the current map container with the one that the map has a reference to
  //   // Ensure the map's div is cleaned up before attaching it
  //   if (container && container !== mapDiv) {
  //     container.innerHTML = '' // Clear any existing content
  //     container.replaceWith(mapDiv) // Replace the container
  //   }
  // }, [map])



  useEffect(() => {
    if (!map || isLargeScreen) return
    
    let timeoutID


    function getGoogleLogoContainer(element) {
      let count = 0
      let currentElement = element

      // Traverse ancestors until the link node is found and return its parent
      while (currentElement) {
        // Since the container should only be 3 elements up, any more than 4 traversials should return a null
        if (count > 4) return null

        // Return if the link element is found
        if (currentElement.tagName === 'A') {
          return currentElement.parentElement
        }

        // Otherwise keep traversing
        count += 1
        currentElement = currentElement.parentElement
      }

      return null
    }



    function getGoogleLogo() {
      const googleLogo = mapOuterContainerRef.current?.querySelector('img[alt="Google"]')
      

      if (googleLogo) {
        clearTimeout(timeoutID)

        const containerElement = getGoogleLogoContainer(googleLogo)

        if (containerElement) {
          containerElement.style.bottom = '60px'
        }
      } else {
        retryCountRef.current += 1

        if (retryCountRef.current < maxRetries) {
          timeoutID = setTimeout(getGoogleLogo, 100) // Retry after 100ms
        } else {
          console.log('Max retries reached, stopping attempts.')
        }
      }
    }


    getGoogleLogo()


    return () => {
      clearTimeout(timeoutID)
    }
  }, [map, isLargeScreen])






  // Handle case where a user clicks a link within an info window
  useEffect(() => {
    const mapContainer = mapOuterContainerRef.current

    mapContainer.addEventListener('click', handleMapClick)


    function handleMapClick(e) {
      let node = e.target

      // If the node that was clicked was the phone link, then don't do anything...
      if (node.id === 'phone-link') {
        return
      }



      // If the node that that was clicked is the play button, then fetch and play the tracks...
      if (node.id === 'play-button') {
        const id = node.dataset.id
        const tracks = node.dataset.tracks


        // Fetch Ckord tracks if present
        if (tracks === 'both' || tracks === 'tracks') {
          const url = '/performances/' + id + '/tracks'


          getAndQueueTracks(url, id, 'performance').catch(error => {
            // TODO: Handle error
          })
        }



        // Fetch Apple tracks if present
        if (tracks === 'both' || tracks === 'apple') {
          show('/services/apple-music/performances/' + id + '/tracks').then(response => {
            const data = response.data

            // Format the Spotify tracks to match the Ckord tracks api
            const ckordTracks = shuffle(convertAppleMusicTracksToCkordFormat(data, '', id, 'Event'))

            // Update the store
            dispatch({type: 'TRACKS_INDEX_SUCCESS', payload: {data: ckordTracks}})
            enqueue(ckordTracks, id)
            addTracksToPerformance(id, ckordTracks)
          })
          .catch(error => {
            // TODO: Handle error
          })
        }


        return
      }


      // Otherwise loop up the ancestry tree to see if the node that was clicked
      // is inside an info winodw, and if so, navigate to the object
      while (node !== null && node !== mapContainer) {
        if (['venue-link', 'map-info-window-content'].includes(node?.id)) {
          const id = node.dataset.id
          const type = node.dataset.type

          navigate(`/${type}s/${id}`)
          break
        }

        node = node.parentNode
      }    
    }

    return () => {
      mapContainer.removeEventListener('click', handleMapClick)
    }
  }, [dispatch, enqueue, navigate, getAndQueueTracks, addTracksToPerformance])




  // Pan to the location when a user clicks a location on a MapListItem
  useEffect(() => {
    if (map && mapCenter.lat && mapCenter.lng) {
      try {
        const latlng = new window.google.maps.LatLng(mapCenter.lat, mapCenter.lng)

        map.panTo(latlng)
      
      } catch(error) {
        console.log('TODO: Handle error in GoogleMap - 0')
      }
    }
  }, [map, mapCenter])




  function closeNotice() {
    setHasScrollNoticeBeenShown(true)
  }




  return (
    <div id='map-outer-container' ref={mapOuterContainerRef} css={styles.root}>
      {!hasScrollNoticeBeenShown && 
        <div css={styles.noticeContainer}>
          <p css={styles.text}>
            Scroll list to load more performances and venues
          </p>

          <div css={styles.iconContainer} onClick={closeNotice}>
            <CloseIcon fontSize='small' />
          </div>
        </div>
      }

      <div id='map-container' style={{width: '100%', height: '100%'}} />
    </div>
  )
}



const mapStateToProps = (state) => {
  return { 
    mapCenter: state.map.center
  }
}



const actions = {enqueue, getAndQueueTracks, setClickedMarkerData, addTracksToPerformance}



export default connect(mapStateToProps, actions)(GoogleMap)



